import * as THREE from 'three';
import { MAX_PROJECTILES, PROJECTILE_SPEED, SHIP_CONFIG } from '../game/config';
import type { BattleEvent, BattleSnapshot, ProjectileState, Quality, ShipKind, Side } from '../game/types';
import { Assets, SHIP_VISUAL_CONFIG } from './models';

const PARTICLE_VERTEX = `
attribute float size;
attribute float alpha;
varying vec3 tint;
varying float opacity;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  gl_PointSize = clamp(size * 430.0 / max(1.0, -mv.z), 1.0, 90.0);
  tint = color; opacity = alpha;
}`;
const PARTICLE_FRAGMENT = `
uniform float soft;
varying vec3 tint;
varying float opacity;
void main() {
  vec2 p = gl_PointCoord - .5;
  float d = length(p) * 2.0;
  d *= 1.0 + soft * .11 * sin(atan(p.y, p.x + .00001) * 5.0 + d * 6.0);
  if (d > 1.0) discard;
  gl_FragColor = vec4(tint, (1.0 - smoothstep(mix(.42, .06, soft), 1.0, d)) * opacity);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;
const TRAIL_VERTEX = `
attribute vec3 direction;
attribute float edge;
attribute float fade;
varying float opacity;
void main() {
  vec3 facing = normalize(cameraPosition - position);
  vec3 side = normalize(cross(normalize(direction), facing));
  vec3 p = position + side * edge * .115;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  opacity = fade;
}`;
const TRAIL_FRAGMENT = `
varying float opacity;
void main() {
  gl_FragColor = vec4(.72, .74, .73, opacity * .46);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

interface Particle {
  x: number; y: number; z: number; vx: number; vy: number; vz: number;
  age: number; life: number; size: number; color: THREE.Color;
  opacity: number; gravity: number; grow: number;
}
interface Flash {
  x: number; y: number; z: number; dx: number; dz: number;
  age: number; life: number; impact: boolean;
}
interface Splinter {
  x: number; y: number; z: number; vx: number; vy: number; vz: number;
  age: number; life: number; spin: number; scale: number;
}
interface TrailPoint { x: number; y: number; z: number; born: number }
export interface CombatShipPose { kind: ShipKind; x: number; y: number; z: number; heading: number }
type CombatEvent = Exclude<BattleEvent, { type: 'result' }>;

function hullBeam(kind: ShipKind, z: number): number {
  const length = SHIP_CONFIG[kind].length, width = length * SHIP_VISUAL_CONFIG[kind].beam, t = z / length + .5;
  return width * .5 * Math.pow(Math.max(0, Math.sin(Math.PI * (.14 + t * .86))), .64) * (1 - Math.pow(Math.max(0, (t - .74) / .26), 2) * .77);
}

/** Match the curved hull and barrel overhang of shipTemplate, including heading and side. */
export function cannonMuzzlePose(kind: ShipKind, side: Side, cannon: number, pose: CombatShipPose): { x: number; y: number; z: number; dx: number; dz: number } {
  const config = SHIP_CONFIG[kind], sign = side === 'port' ? 1 : -1;
  const along = ((cannon + .5) / config.cannons - .5) * config.length * .65;
  const across = (hullBeam(kind, along) + .378) * sign, cosine = Math.cos(pose.heading), sine = Math.sin(pose.heading);
  return { x: pose.x + cosine * across + sine * along, y: pose.y + 1.39, z: pose.z - sine * across + cosine * along, dx: cosine * sign, dz: -sine * sign };
}

/** Height is illustrative; x/z and every trail sample stay on the simulation's straight line. */
export function projectileVisualPosition(projectile: ProjectileState, kind: ShipKind, age = projectile.age): { x: number; y: number; z: number } {
  const duration = Math.min(projectile.ttl, SHIP_CONFIG[kind].range / PROJECTILE_SPEED), offset = age - projectile.age;
  return { x: projectile.x + projectile.vx * offset, y: 1.55 + Math.sin(THREE.MathUtils.clamp(age / duration, 0, 1) * Math.PI) * 2.2, z: projectile.z + projectile.vz * offset };
}

/** Place feedback on the rendered hull surface, without changing the collision event. */
export function hullImpactPoint(x: number, z: number, pose: CombatShipPose): { x: number; y: number; z: number } {
  const dx = x - pose.x, dz = z - pose.z, cosine = Math.cos(pose.heading), sine = Math.sin(pose.heading);
  let across = dx * cosine - dz * sine, along = dx * sine + dz * cosine;
  const distance = Math.hypot(across, along);
  if (distance < .001) { across = 1; along = 0; } else { across /= distance; along /= distance; }
  let near = 0, far = SHIP_CONFIG[pose.kind].length;
  for (let i = 0; i < 16; i++) {
    const mid = (near + far) / 2, localZ = along * mid;
    if (Math.abs(localZ) < SHIP_CONFIG[pose.kind].length * .5 && Math.abs(across * mid) < hullBeam(pose.kind, localZ)) near = mid;
    else far = mid;
  }
  const localX = across * (near + .12), localZ = along * (near + .12);
  return { x: pose.x + localX * cosine + localZ * sine, y: pose.y + 1.25, z: pose.z - localX * sine + localZ * cosine };
}

/** One reusable draw buffer per particle layer; no geometry is allocated by an event. */
class ParticlePool {
  readonly particles: Particle[] = [];
  private readonly geometry: THREE.BufferGeometry;
  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  private readonly sizes: Float32Array;
  private readonly alphas: Float32Array;

  constructor(scene: THREE.Scene, assets: Assets, private readonly capacity: number, smoke: boolean) {
    this.positions = new Float32Array(capacity * 3); this.colors = new Float32Array(capacity * 3);
    this.sizes = new Float32Array(capacity); this.alphas = new Float32Array(capacity);
    this.geometry = assets.geometry(new THREE.BufferGeometry());
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3).setUsage(THREE.DynamicDrawUsage));
    this.geometry.setAttribute('size', new THREE.BufferAttribute(this.sizes, 1).setUsage(THREE.DynamicDrawUsage));
    this.geometry.setAttribute('alpha', new THREE.BufferAttribute(this.alphas, 1).setUsage(THREE.DynamicDrawUsage));
    const points = new THREE.Points(this.geometry, assets.material(new THREE.ShaderMaterial({
      uniforms: { soft: { value: smoke ? 1 : 0 } }, vertexShader: PARTICLE_VERTEX, fragmentShader: PARTICLE_FRAGMENT,
      transparent: true, vertexColors: true, depthWrite: false,
      blending: smoke ? THREE.NormalBlending : THREE.AdditiveBlending,
    })));
    points.name = smoke ? 'combat-smoke' : 'combat-sparks';
    points.frustumCulled = false; points.renderOrder = smoke ? 6 : 7; scene.add(points);
    this.geometry.setDrawRange(0, 0);
  }

  add(particle: Particle, low: boolean): void {
    if (this.particles.length < (low ? Math.floor(this.capacity / 3) : this.capacity)) this.particles.push(particle);
  }

  clear(): void { this.particles.length = 0; this.geometry.setDrawRange(0, 0); }

  trim(low: boolean): void { this.particles.length = Math.min(this.particles.length, low ? Math.floor(this.capacity / 3) : this.capacity); }

  update(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]; p.age += dt;
      if (p.age >= p.life || p.y < -.28) { this.particles[i] = this.particles[this.particles.length - 1]; this.particles.pop(); continue; }
      p.x += p.vx * dt; p.y += p.vy * dt; p.z += p.vz * dt; p.vy -= p.gravity * dt;
    }
    this.particles.forEach((p, i) => {
      this.positions[i * 3] = p.x; this.positions[i * 3 + 1] = p.y; this.positions[i * 3 + 2] = p.z;
      this.colors[i * 3] = p.color.r; this.colors[i * 3 + 1] = p.color.g; this.colors[i * 3 + 2] = p.color.b;
      this.sizes[i] = p.size * (1 + p.age * p.grow);
      this.alphas[i] = p.opacity * Math.pow(Math.max(0, 1 - p.age / p.life), 1.35);
    });
    this.geometry.setDrawRange(0, this.particles.length);
    for (const name of ['position', 'color', 'size', 'alpha']) this.geometry.getAttribute(name).needsUpdate = true;
  }
}

/** Visual-only combat feedback. The simulation remains the sole source of positions and hits. */
export class CombatEffects {
  private readonly smoke: ParticlePool;
  private readonly sparks: ParticlePool;
  private readonly flashes: Flash[] = [];
  private readonly splinters: Splinter[] = [];
  private readonly core: THREE.InstancedMesh;
  private readonly glow: THREE.InstancedMesh;
  private readonly debris: THREE.InstancedMesh;
  private readonly projectiles: THREE.InstancedMesh;
  private readonly trailGeometry: THREE.BufferGeometry;
  private readonly trails = new Map<number, TrailPoint[]>();
  private readonly trailPositions = new Float32Array(MAX_PROJECTILES * 7 * 12);
  private readonly trailDirections = new Float32Array(MAX_PROJECTILES * 7 * 12);
  private readonly trailEdges = new Float32Array(MAX_PROJECTILES * 7 * 4);
  private readonly trailFades = new Float32Array(MAX_PROJECTILES * 7 * 4);
  private readonly dummy = new THREE.Object3D();
  private readonly direction = new THREE.Vector3();
  private readonly up = new THREE.Vector3(0, 1, 0);
  private readonly brightness = new THREE.Color();
  private readonly ivory = new THREE.Color(0xdcd5c4);
  private readonly charcoal = new THREE.Color(0x766a5d);
  private readonly gold = new THREE.Color(0xffc36e);
  private readonly ember = new THREE.Color(0xff7230);
  private readonly foam = new THREE.Color(0xc7eaf5);
  private segments = 0;
  private lastTrailTime = -1;
  private disposed = false;

  constructor(scene: THREE.Scene, assets: Assets, private quality: Quality) {
    this.smoke = new ParticlePool(scene, assets, 256, true); this.sparks = new ParticlePool(scene, assets, 384, false);
    const sphere = assets.geometry(new THREE.SphereGeometry(1, 8, 6));
    this.core = new THREE.InstancedMesh(sphere, assets.material(new THREE.MeshBasicMaterial({ color: 0xffefd0, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false })), 64);
    this.glow = new THREE.InstancedMesh(sphere, assets.material(new THREE.MeshBasicMaterial({ color: 0xff8b26, opacity: .52, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false })), 64);
    this.debris = new THREE.InstancedMesh(assets.geometry(new THREE.BoxGeometry(.18, .13, .7)), assets.material(new THREE.MeshStandardMaterial({ color: 0xaf793d, roughness: .93 })), 64);
    this.projectiles = new THREE.InstancedMesh(assets.geometry(new THREE.SphereGeometry(.34, 8, 6)), assets.material(new THREE.MeshStandardMaterial({ color: 0x655440, emissive: 0xffa145, emissiveIntensity: .46, metalness: .35, roughness: .55 })), MAX_PROJECTILES);
    this.core.name = 'combat-muzzle-core'; this.glow.name = 'combat-muzzle-glow'; this.debris.name = 'combat-debris'; this.projectiles.name = 'combat-projectiles';
    for (const value of [this.core, this.glow, this.debris, this.projectiles]) {
      value.count = 0; value.instanceMatrix.setUsage(THREE.DynamicDrawUsage); value.frustumCulled = false; scene.add(value);
    }
    this.core.renderOrder = 5; this.glow.renderOrder = 4;
    this.trailGeometry = assets.geometry(new THREE.BufferGeometry());
    this.trailGeometry.setAttribute('position', new THREE.BufferAttribute(this.trailPositions, 3).setUsage(THREE.DynamicDrawUsage));
    this.trailGeometry.setAttribute('direction', new THREE.BufferAttribute(this.trailDirections, 3).setUsage(THREE.DynamicDrawUsage));
    this.trailGeometry.setAttribute('edge', new THREE.BufferAttribute(this.trailEdges, 1).setUsage(THREE.DynamicDrawUsage));
    this.trailGeometry.setAttribute('fade', new THREE.BufferAttribute(this.trailFades, 1).setUsage(THREE.DynamicDrawUsage));
    const indices: number[] = [];
    for (let i = 0; i < MAX_PROJECTILES * 7; i++) { const a = i * 4; indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
    this.trailGeometry.setIndex(indices); this.trailGeometry.setDrawRange(0, 0);
    const trails = new THREE.Mesh(this.trailGeometry, assets.material(new THREE.ShaderMaterial({ vertexShader: TRAIL_VERTEX, fragmentShader: TRAIL_FRAGMENT, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide })));
    trails.name = 'combat-trails'; trails.frustumCulled = false; trails.renderOrder = 4; scene.add(trails);
  }

  setQuality(quality: Quality): void {
    this.quality = quality; this.smoke.trim(quality === 'low'); this.sparks.trim(quality === 'low');
    this.flashes.length = Math.min(this.flashes.length, quality === 'low' ? 32 : 64);
    this.splinters.length = Math.min(this.splinters.length, quality === 'low' ? 24 : 64);
  }

  clear(): void {
    this.smoke.clear(); this.sparks.clear(); this.flashes.length = 0; this.splinters.length = 0; this.trails.clear();
    this.core.count = 0; this.glow.count = 0; this.debris.count = 0; this.projectiles.count = 0;
    this.trailGeometry.setDrawRange(0, 0); this.segments = 0; this.lastTrailTime = -1;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true; this.clear();
    // Instance buffers belong to each mesh, separately from the shared geometry.
    for (const mesh of [this.core, this.glow, this.debris, this.projectiles]) mesh.dispose();
  }

  emit(event: CombatEvent, pose: CombatShipPose | undefined): void {
    if (this.disposed) return;
    const low = this.quality === 'low';
    if (event.type === 'fire') {
      const kind = pose?.kind ?? 'frigate', config = SHIP_CONFIG[kind], side = event.side === 'port' ? 1 : -1;
      const heading = pose?.heading ?? event.heading - side * Math.PI / 2;
      const ship = pose ?? { kind, x: event.x, y: .16, z: event.z, heading };
      for (let cannon = 0; cannon < config.cannons; cannon++) {
        const { x, y, z, dx, dz } = cannonMuzzlePose(kind, event.side, cannon, ship);
        this.flash({ x, y, z, dx, dz, age: 0, life: .18, impact: false });
        for (let i = 0; i < (low ? 1 : 3); i++) this.smoke.add({ x: x + dx * .6, y, z: z + dz * .6, vx: dx * (2.8 + i), vy: 1.1 + i * .2, vz: dz * (2.8 + i), age: 0, life: 1.2 + i * .17, size: 1.4 + i * .3, color: this.ivory, opacity: .43, gravity: -.18, grow: 1.6 }, low);
        for (let i = 0; i < (low ? 2 : 4); i++) this.sparks.add({ x, y, z, vx: dx * (8 + i * 1.7) + Math.sin(i * 2.399), vy: .7 + i * .25, vz: dz * (8 + i * 1.7) + Math.cos(i * 2.399), age: 0, life: .16 + i * .025, size: .42, color: i % 2 ? this.ember : this.gold, opacity: .94, gravity: 2, grow: -.4 }, low);
      }
      return;
    }
    if (event.type === 'hit') {
      const impact = this.impactPoint(event, pose), dx = impact.x - (pose?.x ?? event.x), dz = impact.z - (pose?.z ?? event.z);
      this.flash({ ...impact, dx, dz, age: 0, life: .23, impact: true });
      const amount = low ? 6 : 16;
      for (let i = 0; i < amount; i++) {
        const angle = i * 2.399, speed = 3.5 + i % 5;
        this.sparks.add({ ...impact, vx: Math.cos(angle) * speed, vy: 2 + i % 4 * 1.15, vz: Math.sin(angle) * speed, age: 0, life: .35 + i % 4 * .1, size: .34 + i % 3 * .1, color: i % 3 ? this.gold : this.ember, opacity: .9, gravity: 10, grow: -.3 }, low);
        if (i < (low ? 3 : 8)) this.splinter({ ...impact, vx: Math.cos(angle) * (3 + i % 4), vy: 3 + i % 3 * 1.7, vz: Math.sin(angle) * (3 + i % 4), age: 0, life: 1.2, spin: 3 + i, scale: .7 + i % 3 * .3 });
      }
      for (let i = 0; i < (low ? 2 : 5); i++) this.smoke.add({ ...impact, vx: Math.cos(i * 2.399) * 1.1, vy: 1.5 + i * .15, vz: Math.sin(i * 2.399) * 1.1, age: 0, life: 1 + i * .15, size: 1.25 + i * .16, color: i % 2 ? this.ivory : this.charcoal, opacity: .49, gravity: -.12, grow: 1.15 }, low);
      return;
    }
    for (let i = 0; i < (low ? 18 : 38); i++) {
      const angle = i * 2.399, speed = 2 + i % 6 * .7;
      this.sparks.add({ x: event.x + Math.cos(angle) * 2, y: .35, z: event.z + Math.sin(angle) * 2, vx: Math.cos(angle) * speed, vy: 3 + i % 5 * .7, vz: Math.sin(angle) * speed, age: 0, life: 1 + i % 4 * .15, size: .8 + i % 3 * .2, color: this.foam, opacity: .75, gravity: 7, grow: .4 }, low);
      if (i % 4 === 0) this.splinter({ x: event.x, y: 1, z: event.z, vx: Math.cos(angle) * speed, vy: 4 + i % 5, vz: Math.sin(angle) * speed, age: 0, life: 1.7, spin: 4 + i % 5, scale: 1.1 + i % 3 * .35 });
    }
  }

  private impactPoint(event: Extract<CombatEvent, { type: 'hit' }>, pose: CombatShipPose | undefined): { x: number; y: number; z: number } {
    if (!pose) return { x: event.x, y: 1.45, z: event.z };
    return hullImpactPoint(event.x, event.z, pose);
  }

  private flash(value: Flash): void { if (this.flashes.length < (this.quality === 'low' ? 32 : 64)) this.flashes.push(value); }
  private splinter(value: Splinter): void { if (this.splinters.length < (this.quality === 'low' ? 24 : 64)) this.splinters.push(value); }

  update(snapshot: BattleSnapshot | null, dt: number): void {
    if (this.disposed) return;
    dt = snapshot?.phase === 'paused' ? 0 : THREE.MathUtils.clamp(dt, 0, .05);
    this.smoke.update(dt); this.sparks.update(dt);
    for (let i = this.flashes.length - 1; i >= 0; i--) {
      const flash = this.flashes[i]; flash.age += dt;
      if (flash.age >= flash.life) { this.flashes[i] = this.flashes[this.flashes.length - 1]; this.flashes.pop(); }
    }
    this.flashes.forEach((flash, i) => {
      const age = flash.age / flash.life, fade = Math.pow(1 - age, 1.7), pulse = 1 + Math.sin(age * Math.PI) * .4;
      this.direction.set(flash.dx, 0, flash.dz); if (this.direction.lengthSq() < .001) this.direction.set(1, 0, 0); this.direction.normalize();
      this.dummy.quaternion.setFromUnitVectors(this.up, this.direction);
      this.dummy.position.set(flash.x, flash.y, flash.z).addScaledVector(this.direction, flash.impact ? 0 : .6);
      this.dummy.scale.set(flash.impact ? .54 * pulse : .27 * pulse, flash.impact ? .54 * pulse : .8 * pulse, flash.impact ? .54 * pulse : .27 * pulse);
      this.dummy.updateMatrix(); this.core.setMatrixAt(i, this.dummy.matrix); this.brightness.setRGB(fade, fade, fade); this.core.setColorAt(i, this.brightness);
      this.dummy.scale.set(flash.impact ? 1.15 * pulse : .65 * pulse, flash.impact ? 1.15 * pulse : 1.5 * pulse, flash.impact ? 1.15 * pulse : .65 * pulse);
      this.dummy.updateMatrix(); this.glow.setMatrixAt(i, this.dummy.matrix); this.glow.setColorAt(i, this.brightness);
    });
    this.core.count = this.flashes.length; this.glow.count = this.flashes.length;
    for (const mesh of [this.core, this.glow]) { mesh.instanceMatrix.needsUpdate = true; if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true; }
    for (let i = this.splinters.length - 1; i >= 0; i--) {
      const p = this.splinters[i]; p.age += dt;
      if (p.age >= p.life || p.y < -.28) { this.splinters[i] = this.splinters[this.splinters.length - 1]; this.splinters.pop(); continue; }
      p.x += p.vx * dt; p.y += p.vy * dt; p.z += p.vz * dt; p.vy -= 10 * dt;
    }
    this.splinters.forEach((p, i) => {
      this.dummy.position.set(p.x, p.y, p.z); this.dummy.rotation.set(p.age * p.spin, i * 2.399 + p.age * p.spin * .6, p.age * p.spin * .7); this.dummy.scale.setScalar(p.scale);
      this.dummy.updateMatrix(); this.debris.setMatrixAt(i, this.dummy.matrix);
    });
    this.debris.count = this.splinters.length; this.debris.instanceMatrix.needsUpdate = true;
    this.updateProjectiles(snapshot);
  }

  private updateProjectiles(snapshot: BattleSnapshot | null): void {
    if (!snapshot) { this.projectiles.count = 0; this.trails.clear(); this.segments = 0; this.trailGeometry.setDrawRange(0, 0); return; }
    const changed = snapshot.time > this.lastTrailTime, ids = new Set<number>(); let segment = 0;
    this.projectiles.count = Math.min(snapshot.projectiles.length, MAX_PROJECTILES);
    snapshot.projectiles.slice(0, MAX_PROJECTILES).forEach((projectile, index) => {
      ids.add(projectile.id);
      const owner = snapshot.ships.find(ship => ship.id === projectile.ownerId), kind = owner?.kind ?? 'frigate';
      const { y } = projectileVisualPosition(projectile, kind);
      this.dummy.position.set(projectile.x, y, projectile.z); this.dummy.rotation.set(0, 0, 0); this.dummy.scale.setScalar(1); this.dummy.updateMatrix(); this.projectiles.setMatrixAt(index, this.dummy.matrix);
      let points = this.trails.get(projectile.id);
      if (!points) {
        const elapsed = Math.min(.035, projectile.age);
        points = [{ ...projectileVisualPosition(projectile, kind, projectile.age - elapsed), born: snapshot.time - elapsed }]; this.trails.set(projectile.id, points);
      }
      if (changed || points.length === 1) {
        points.push({ x: projectile.x, y, z: projectile.z, born: snapshot.time });
        while (points.length > 8 || (points.length > 2 && snapshot.time - points[0].born > .12)) points.shift();
      }
      for (let i = 1; i < points.length; i++) {
        const a = points[i - 1], b = points[i], dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
        if (Math.hypot(dx, dy, dz) < .0001) continue;
        for (let j = 0; j < 4; j++) {
          const point = j < 2 ? a : b, n = segment * 12 + j * 3, attribute = segment * 4 + j;
          this.trailPositions[n] = point.x; this.trailPositions[n + 1] = point.y; this.trailPositions[n + 2] = point.z;
          this.trailDirections[n] = dx; this.trailDirections[n + 1] = dy; this.trailDirections[n + 2] = dz;
          this.trailEdges[attribute] = j % 2 === 0 ? -1 : 1;
          this.trailFades[attribute] = Math.max(0, 1 - (snapshot.time - point.born) / .12);
        }
        segment++;
      }
    });
    this.trails.forEach((_trail, id) => { if (!ids.has(id)) this.trails.delete(id); });
    this.segments = segment; this.lastTrailTime = snapshot.time;
    this.projectiles.instanceMatrix.needsUpdate = true; this.trailGeometry.setDrawRange(0, segment * 6);
    for (const name of ['position', 'direction', 'edge', 'fade']) this.trailGeometry.getAttribute(name).needsUpdate = true;
  }

  getStats(): { activeParticles: number; muzzleFlashes: number; impactFlashes: number; woodSplinters: number; projectiles: number; projectileTrails: number; trailSegments: number } {
    return { activeParticles: this.smoke.particles.length + this.sparks.particles.length, muzzleFlashes: this.flashes.filter(flash => !flash.impact).length, impactFlashes: this.flashes.filter(flash => flash.impact).length, woodSplinters: this.splinters.length, projectiles: this.projectiles.count, projectileTrails: this.trails.size, trailSegments: this.segments };
  }
}
